import React, { useState } from "react";
import { useApolloClient, useMutation } from "@apollo/react-hooks";
import { Button, Drawer, Icon, Upload } from "antd";

import { POST_ADD_ATTACHMENT, POST_DELETE_ATTACHMENT } from "graphql/mutation/post-gql";
import { GET_POST } from "graphql/query/post-gql";

import { errorNotification, successNotification } from "components/result";
import Icons from "components/icons";
import { Localize } from "components/service";
import useAttachmentActions from "components/attachments/use-attachment-actions";
import { isAttachmentPrimaryClick } from "components/attachments/attachment-actions";
import { getAttachmentFileName, getAttachmentPath } from "utils/attachment";

import './post-upload-attachments.css';


export const UploadAttachments = (props) => {

    const {
        defaultAttachments,
        postId,
        postSlug,
        countSendedFiles,
        setCountSendedFiles,
        hideDrawer
    } =  props;

    const client = useApolloClient();
    const attachmentActions = useAttachmentActions(postId);

    const [addAttachment, { loading }] = useMutation(POST_ADD_ATTACHMENT);
    const [deleteAttachment] = useMutation(POST_DELETE_ATTACHMENT);

    const { Dragger } = Upload;

    const fileList = defaultAttachments.map(attachment => {
        const { id } = attachment;
        return (
            {
                uid : id,
                name : getAttachmentFileName(attachment),
                url : getAttachmentPath(id),
                status : 'done'
            }
        )
    });

    const formAttachmentsProps = {
        beforeUpload :  (file) => {

            setCountSendedFiles(countSendedFiles + 1 );

            addAttachment({
                variables : {
                    post_id : postId,
                    files: [file]
                },
                update(cache, { data }) {

                    const {addAttached} = data;
                    const {label, message} = addAttached;

                    const {post} = client.readQuery({
                        query: GET_POST,
                        variables: {
                            slug: postSlug,
                        }
                    });

                    client.writeQuery({
                        query: GET_POST,
                        data: {
                            post: {...post, ...addAttached.post}
                        },
                    });

                    setCountSendedFiles(countSendedFiles - 1 );

                    successNotification({
                        title: label,
                        description: message
                    });

                }
            }).catch((error) => {
                setCountSendedFiles(countSendedFiles - 1 );
                errorNotification(error);
            });

            return false;
        },
        onRemove: (file) => {

            if(file.status === 'done') {

                deleteAttachment({
                    variables: {
                        attached_id: file.uid
                    },
                    update: (cache, {data}) => {

                        const {
                            deleteAttached : {
                                label,
                                message
                            }
                        } = data;

                        const {post} = client.readQuery({
                            query: GET_POST,
                            variables: {
                                slug: postSlug,
                            }
                        });

                        const newMedia = post.attachments.filter(item => item.id !== file.uid);

                        client.writeQuery({
                            query: GET_POST,
                            data: {
                                post: {...post, attachments: [...newMedia]}
                            },
                        });

                        successNotification({
                            title: label,
                            description: message
                        });

                    }
                }).catch((error) => {
                    errorNotification(error);
                });
            }
        },
        fileList: fileList,
        showUploadList: {
            showPreviewIcon: false,
        },
        multiple : true
    };

    return(
        <div className="post-update-attachments" onClickCapture={event => {
            if (!isAttachmentPrimaryClick(event)) return;
            const link = event.target.closest("a.ant-upload-list-item-name");
            if (!link) return;
            const attachment = defaultAttachments.find(item => getAttachmentPath(item.id) === link.getAttribute("href"));
            if (!attachment) return;
            event.preventDefault();
            event.stopPropagation();
            attachmentActions.open(attachment);
        }}>
            <Dragger {...formAttachmentsProps}>
                <div>
                    { loading ? <Icon type="loading" /> : <div className="site-badge"><Icons.Plus /></div> }
                    <Localize wrap>POST.Dragger_Text_Attach</Localize>
                </div>
                <div className="upload-rules site-label-text-color">
                    <Localize size={ 100 } >FORM_RULES.Attachment_Size</Localize>
                </div>
            </Dragger>

            {defaultAttachments.map(attachment => (
                <React.Fragment key={attachment.id}>
                    {attachmentActions.loading[attachment.id] && (
                        <div className="attachment-action-error" role="status">
                            <Icon type="loading" /> {getAttachmentFileName(attachment)}: <Localize>GLOBAL.Text_Loading</Localize>
                        </div>
                    )}
                    {attachmentActions.errors[attachment.id] && (
                        <div className="attachment-action-error" role="alert">
                            {getAttachmentFileName(attachment)}: <Localize>{attachmentActions.errors[attachment.id]}</Localize>
                            <button type="button" className="company-color" onClick={() => attachmentActions.open(attachment)}>
                                <Localize>ATTACHMENTS.Button_Retry</Localize>
                            </button>
                        </div>
                    )}
                </React.Fragment>
            ))}

            <div className="hide-drawer">
                <Button
                    type="primary"
                    className="rounded-button"
                    onClick={ hideDrawer }
                >
                    <Localize wrap>GLOBAL.Button_Text_Done</Localize>
                </Button>
            </div>
        </div>
    );
};

const PostUploadAttachments = (props) => {

    const { defaultAttachments } = props;

    const [countSendedFiles, setCountSendedFiles] = useState(0);
    const [showDrawer, setShowDrawer] = useState(false);


    return (
        <div className="post-update-attachments-wrap">
            <Localize wrap="h4" attachmentCount={ defaultAttachments.length }>
                POST.Attachment_Count
            </Localize>
            <Button
                className="rounded-button"
                onClick={ () => setShowDrawer( true )}
            >
                <Localize>POST.Title_AttachDocuments</Localize>
            </Button>
            <Drawer
                className="post-update-drawer"
                title={
                    <>
                        <Localize wrap="h4">POST.Drawer_Title_Attachments</Localize>
                        <button
                            className="close-drawer"
                            onClick={ () => setShowDrawer( false )}
                        >
                            <Icons.Close />
                        </button>
                    </>
                }
                placement="right"
                closable={ false }
                onClose={ () => setShowDrawer( false )}
                visible={showDrawer}
            >
                <UploadAttachments
                    { ...props }
                    countSendedFiles={countSendedFiles}
                    setCountSendedFiles={setCountSendedFiles}
                    hideDrawer={ () => setShowDrawer( false ) }
                />
            </Drawer>
        </div>
    );

};

export default PostUploadAttachments;
